import type { CastRefEventStore } from "applesauce-common/casts/cast";
import { EventCast } from "applesauce-common/casts/cast";
import { getOrComputeCachedValue } from "applesauce-core/helpers";
import { getTagValue, type KnownEvent } from "applesauce-core/helpers/event";
import type { NostrEvent } from "nostr-tools";

export const CONTAINER_REPOSITORY_KIND = 30624 as const;

type ContainerRepositoryEvent = KnownEvent<typeof CONTAINER_REPOSITORY_KIND>;

const OCI_NAME_COMPONENT = /^[a-z0-9]+((\.|_|__|-+)[a-z0-9]+)*$/;
const OCI_TAG = /^[a-zA-Z0-9_][a-zA-Z0-9._-]{0,127}$/;
const SHA256 = /^[0-9a-f]{64}$/;

const NameSymbol = Symbol.for("container-repository-name");
const TitleSymbol = Symbol.for("container-repository-title");
const DescriptionSymbol = Symbol.for("container-repository-description");
const SourceSymbol = Symbol.for("container-repository-source");
const ServersSymbol = Symbol.for("container-repository-servers");
const RepoCoordsSymbol = Symbol.for("container-repository-repo-coords");
const VersionsSymbol = Symbol.for("container-repository-versions");
const CoordinateSymbol = Symbol.for("container-repository-coordinate");

export interface ContainerVersion {
  tag: string;
  sha256: string;
}

function repeatedTagValues(event: NostrEvent, name: string): string[] {
  return event.tags
    .filter(([tagName, value]) => tagName === name && !!value)
    .map(([, value]) => value);
}

function safeHttpUrl(value: string | undefined): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
}

export function isValidContainerRepository(
  event: NostrEvent,
): event is ContainerRepositoryEvent {
  const name = getTagValue(event, "d");
  return (
    event.kind === CONTAINER_REPOSITORY_KIND &&
    !!name &&
    OCI_NAME_COMPONENT.test(name)
  );
}

/** Typed view of an ncontainer kind:30624 repository event. */
export class ContainerRepository extends EventCast<ContainerRepositoryEvent> {
  constructor(event: NostrEvent, store: CastRefEventStore) {
    if (!isValidContainerRepository(event)) {
      throw new Error("Invalid container repository event");
    }
    super(event, store);
  }

  get pubkey(): string {
    return this.event.pubkey;
  }

  get name(): string {
    return getOrComputeCachedValue(
      this.event,
      NameSymbol,
      () => getTagValue(this.event, "d")!,
    );
  }

  get title(): string {
    return getOrComputeCachedValue(
      this.event,
      TitleSymbol,
      () => getTagValue(this.event, "title")?.trim() || this.name,
    );
  }

  get description(): string | undefined {
    return getOrComputeCachedValue(
      this.event,
      DescriptionSymbol,
      () => getTagValue(this.event, "description")?.trim() || undefined,
    );
  }

  get source(): string | undefined {
    return getOrComputeCachedValue(this.event, SourceSymbol, () =>
      safeHttpUrl(getTagValue(this.event, "source")),
    );
  }

  get servers(): string[] {
    return getOrComputeCachedValue(this.event, ServersSymbol, () => [
      ...new Set(
        repeatedTagValues(this.event, "server").flatMap((value) => {
          const url = safeHttpUrl(value);
          return url ? [url] : [];
        }),
      ),
    ]);
  }

  get repoCoords(): string[] {
    return getOrComputeCachedValue(this.event, RepoCoordsSymbol, () => [
      ...new Set(repeatedTagValues(this.event, "a")),
    ]);
  }

  get versions(): ContainerVersion[] {
    return getOrComputeCachedValue(this.event, VersionsSymbol, () => {
      const versions = new Map<string, ContainerVersion>();
      for (const [name, tag, sha256] of this.event.tags) {
        if (
          name !== "tag" ||
          !tag ||
          !sha256 ||
          !OCI_TAG.test(tag) ||
          !SHA256.test(sha256)
        ) {
          continue;
        }
        versions.set(tag, { tag, sha256 });
      }
      return [...versions.values()];
    });
  }

  get coordinate(): string {
    return getOrComputeCachedValue(
      this.event,
      CoordinateSymbol,
      () => `${CONTAINER_REPOSITORY_KIND}:${this.pubkey}:${this.name}`,
    );
  }
}
